import { useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { usePrivacy } from '@/lib/privacy';
import { Button, ErrorBox, Label, Modal, Spinner, fieldStyles } from './ui';
import { EyeIcon } from './icons';
import s from './PrivacyToggle.module.css';

/**
 * Header control for private mode. Hiding is free — one click. Showing goes
 * through the PIN prompt, which is the whole point: a live session on its own
 * isn't enough to see the figures.
 */
export function PrivacyToggle({ className }: { className?: string }) {
  const { masked, hide, requestUnlock } = usePrivacy();

  return (
    <button
      type="button"
      className={className}
      onClick={() => (masked ? requestUnlock() : hide())}
      aria-pressed={masked}
      aria-label={masked ? 'Show amounts' : 'Hide amounts'}
      title={masked ? 'Amounts hidden — click to unlock' : 'Amounts visible — click to hide'}
    >
      <EyeIcon off={masked} />
    </button>
  );
}

/**
 * The unlock prompt. Comes up by itself when the app opens with a PIN set,
 * and on demand from the header toggle.
 *
 * Skipping is a first-class answer, not a dead end — browsing masked is the
 * normal way to show someone the app.
 */
export function PrivacyPrompt() {
  const { prompting, pinSet, dismiss, unlockWithPin, unlockWithPassword } = usePrivacy();
  if (!prompting) return null;
  return (
    <PromptBody
      pinSet={pinSet}
      onDismiss={dismiss}
      unlockWithPin={unlockWithPin}
      unlockWithPassword={unlockWithPassword}
    />
  );
}

function PromptBody({
  pinSet,
  onDismiss,
  unlockWithPin,
  unlockWithPassword,
}: {
  pinSet: boolean;
  onDismiss: () => void;
  unlockWithPin: (pin: string) => Promise<void>;
  unlockWithPassword: (password: string) => Promise<void>;
}) {
  // Without a PIN the password is the only way through, so start there.
  const [usePassword, setUsePassword] = useState(!pinSet);
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  const ready = usePassword ? password.length > 0 : pin.length === 4;

  async function attempt(secret: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await (usePassword ? unlockWithPassword(secret) : unlockWithPin(secret));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t check that. Try again.');
      setPin('');
      setPassword('');
      setBusy(false);
      pinRef.current?.focus();
    }
  }

  /** Fires on the fourth digit — a PIN pad shouldn't need a separate button. */
  function onPinChange(next: string) {
    const digits = next.replace(/\D/g, '').slice(0, 4);
    setPin(digits);
    setError(null);
    if (digits.length === 4) void attempt(digits);
  }

  return (
    <Modal title="Show amounts" onClose={onDismiss}>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void attempt(usePassword ? password : pin);
        }}
      >
        <div className={s.blurb}>
          Balances and amounts are hidden. {usePassword ? 'Enter your password' : 'Enter your PIN'} to
          show them for this session — they hide again when you reload or sign out.
        </div>

        {usePassword ? (
          <div>
            <Label htmlFor="privacy-password">password</Label>
            <input
              id="privacy-password"
              type="password"
              className={fieldStyles.input}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              autoComplete="current-password"
              autoFocus
            />
          </div>
        ) : (
          <div>
            <Label htmlFor="privacy-pin">pin</Label>
            <input
              id="privacy-pin"
              ref={pinRef}
              className={s.pin}
              value={pin}
              onChange={(e) => onPinChange(e.target.value)}
              inputMode="numeric"
              autoComplete="off"
              type="password"
              maxLength={4}
              placeholder="••••"
              aria-label="4-digit PIN"
              autoFocus
            />
          </div>
        )}

        {error ? <ErrorBox>{error}</ErrorBox> : null}

        <div className={s.actions}>
          {pinSet ? (
            <button
              type="button"
              className={s.switch}
              onClick={() => {
                setUsePassword((v) => !v);
                setError(null);
                setPin('');
                setPassword('');
              }}
            >
              {usePassword ? 'Use PIN instead' : 'Use password instead'}
            </button>
          ) : null}
          <Button type="button" onClick={onDismiss}>
            Skip
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !ready}>
            {busy ? <Spinner /> : 'Show'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
